import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams, usePathname } from "next/navigation";

import { useSnapshot } from "valtio";
import { I18nUtil } from "../utils";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import type {
  RecommendQuestionRequest,
  RecommendQuestionResponse,
} from "@/src/services/recommendQuestionsService";
import { homeQueryKeys } from "@/src/services/homeQueryKeys";
import { rootStore } from "@/src/store";
import {
  type StockType,
  getStockTypeFromPathname,
  DEFAULT_STOCK_TYPE,
} from "@/src/utils/stockType";

interface RecommendQuestionOptions {
  enabled?: boolean;
  staleTime?: number;
  key?: string;
  initialData?: RecommendQuestionResponse;
  initialDataUpdatedAt?: number;
  locale?: string;
  /** 股票市场类型（US | HK），当 type 为 "stock" 时使用
   * @note 优先使用传入的值，如果没有传入则从路由 pathname 解析
   */
  stockType?: StockType;
}
export const IconMap = ["edgen-Sources", "stats", "candles", "cart-add"];

export const useRecommendQuestion = (
  recommendRequest: RecommendQuestionRequest & { related?: string },
  options?: RecommendQuestionOptions,
) => {
  const params = useParams<{ locale: string }>();
  const paramsLocale = (params.locale as string) || "en";
  const {
    locale = paramsLocale,
    key = "",
    stockType: propsStockType,
  } = options || {};
  const language = I18nUtil.getBackgroundLanguageKey(locale);
  const { openDataApi } = useOspApiClientProvider();
  const { type } = useSnapshot(rootStore.home);
  // 优先使用传入的 stockType，如果没有传入则从 pathname 解析
  // 注意：即使传入了 stockType，usePathname 仍需要调用（hook 规则），但结果不会被使用
  const pathname = usePathname();
  const stockType: StockType =
    propsStockType ?? getStockTypeFromPathname(pathname) ?? DEFAULT_STOCK_TYPE;

  // 检查 API 是否准备好
  const isApiReady = !!openDataApi?.recommend;
  const isEnabled = options?.enabled ?? true;

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: homeQueryKeys.recommendQuestion(
      type,
      recommendRequest,
      key,
      stockType,
    ),
    enabled: isApiReady && isEnabled,
    staleTime: options?.staleTime,
    initialData: options?.initialData,
    initialDataUpdatedAt: options?.initialDataUpdatedAt,
    queryFn: async () => {
      if (!openDataApi?.recommend) {
        1;
        throw new Error("OpenDataApi recommend is not available");
      }
      const res = await openDataApi.recommend.getRecommend(
        recommendRequest as unknown as {
          scopes: Record<string, number>;
          related?: string;
        },
      );
      return res?.data?.data || {};
    },
  });

  const questions = useMemo(() => {
    const keys = Object.keys(data || {});

    return keys
      .flatMap((k) => {
        return (
          data?.[k]?.map((item, index) => {
            const content =
              item.multi_language_contents?.find(
                (content) => content.language === language,
              ) || item.multi_language_contents?.[0];
            const prompt = item?.multi_language_contents?.find(
              (content) => content.language === "en-US",
            )?.title;
            const key = item?.multi_language_contents?.find(
              (content) => content.language === "en-US",
            )?.display_title;
            const source_id = (item as any)?.source_id || "";
            const agent_id = (item as any)?.agent_id || "";
            return {
              id: item.id || "",
              question: content?.title || "",
              source_id, // 后端新增的 source_id 字段（是否有关联的新闻）
              scope: k, // 推荐问题的 scope（TOKEN_CHAT_REC 或 STOCK_CHAT_REC）
              // prompt做替换
              title: content?.display_title,
              prompt: prompt,
              prompt_translation: content?.title,
              description: content?.description,
              icon: IconMap[index],
              language,
              key,
              dataTrackingScope: `recommend-question-${content?.title}${source_id ? `-${source_id}` : ""}${agent_id ? `-${agent_id}` : ""}`,
            };
          }) || []
        );
      })
      .filter((item) => item.question);
  }, [data, language]);

  return {
    data,
    questions,
    isLoading: isLoading || isFetching || !isApiReady, // 合并两个状态，确保任何请求时都显示 loading
    isError,
    refetch,
  };
};
